import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import type { ScheduledSend, Snooze } from '@/api/types';
import { Snackbar } from '@/components/Snackbar';
import { useToastSlot } from '@/components/toastStack';
import { formatWhen } from './when';

/**
 * "Scheduled for Tue, Sep 24, 8:00 AM · View · Undo" and "Snoozed until …
 * · Undo" (ADR 0076 §15–16): the word that a send was scheduled or mail was
 * snoozed, with the way back.
 *
 * Its own region in the shell's toast stack, like "Added to Tasks": an
 * archive's Undo and this one can both be on screen, and each undoes its own
 * thing. Owned by the view that did it (the mail view, or the Scheduled and
 * Snoozed views), so it needs no host of its own.
 */

const NOTICE_MS = 8000;

interface Action {
  label: string;
  run: () => void;
}

interface Notice {
  id: number;
  text: string;
  action?: Action;
  secondary?: Action;
}

/** What a finished snooze hands the snackbar. */
export interface SnoozeDone {
  jobs: Snooze[];
  /** Per-message refusals, as sentences. */
  failed: string[];
  wakeAt: Date;
  /** Every mailbox a request went to. */
  accountIds: string[];
}

/** The reads a snooze or unsnooze changes: the lists it left or came back to, the counts, the views. */
export function invalidateSnoozed(client: QueryClient, accountIds: Iterable<string>): void {
  for (const accountId of new Set(accountIds)) {
    const keys = queryKeys.account(accountId);
    void client.invalidateQueries({ queryKey: keys.messagesScope() });
    void client.invalidateQueries({ queryKey: keys.folders() });
    void client.invalidateQueries({ queryKey: keys.schedulingScope() });
  }
  void client.invalidateQueries({ queryKey: queryKeys.unifiedMessagesScope() });
}

/** The sentence for a snooze. */
export function snoozedText(done: Pick<SnoozeDone, 'jobs' | 'failed' | 'wakeAt'>): string {
  const count = done.jobs.length;
  const when = formatWhen(done.wakeAt);
  const parts = [count === 1 ? `Snoozed until ${when}` : `${count} messages snoozed until ${when}`];
  if (done.failed.length > 0) parts.push(`${done.failed.length} couldn’t be snoozed`);
  return parts.join(' · ');
}

export interface SchedulingSnackbar {
  /** A plain sentence, optionally with one action. */
  say: (text: string, action?: Action) => void;
  announceSnoozed: (done: SnoozeDone) => void;
  /**
   * A scheduled send. Undo cancels it (the copy goes back to Drafts) and hands
   * the draft to `reopen`; View goes to the Scheduled view.
   */
  announceScheduled: (
    job: ScheduledSend,
    accountId: string,
    options: { reopen: (accountId: string, draftId: string) => void; view: () => void },
  ) => void;
  snackbar: ReactNode;
}

export function useSchedulingSnackbar(): SchedulingSnackbar {
  const api = useApi();
  const client = useQueryClient();
  const slot = useToastSlot();
  const [notice, setNotice] = useState<Notice | undefined>();
  const [held, setHeld] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!notice || held) return;
    const timer = setTimeout(() => setNotice(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice, held]);

  const say = useCallback((text: string, action?: Action, secondary?: Action) => {
    seq.current += 1;
    setNotice({ id: seq.current, text, ...(action ? { action } : {}), ...(secondary ? { secondary } : {}) });
  }, []);

  const announceSnoozed = useCallback(
    (done: SnoozeDone) => {
      if (done.jobs.length === 0) {
        say(done.failed[0] ?? 'Nothing was snoozed.');
        return;
      }
      say(snoozedText(done), {
        label: 'Undo',
        run: () => {
          void Promise.allSettled(done.jobs.map((job) => api.scheduling.unsnooze(job.accountId, job.id))).then(
            (results) => {
              invalidateSnoozed(client, done.jobs.map((job) => job.accountId));
              const failed = results.filter((result) => result.status === 'rejected').length;
              if (failed === 0) say(done.jobs.length === 1 ? 'Unsnoozed' : `${done.jobs.length} messages unsnoozed`);
              else say(`${failed} couldn’t be unsnoozed. Unsnooze ${failed === 1 ? 'it' : 'them'} from Snoozed.`);
            },
          );
        },
      });
    },
    [api, client, say],
  );

  const announceScheduled = useCallback<SchedulingSnackbar['announceScheduled']>(
    (job, accountId, options) => {
      say(
        `Scheduled for ${formatWhen(new Date(job.sendAt))}`,
        {
          label: 'Undo',
          run: () => {
            void api.scheduling.cancelScheduled(accountId, job.id).then(
              (cancelled) => {
                const keys = queryKeys.account(accountId);
                void client.invalidateQueries({ queryKey: keys.schedulingScope() });
                void client.invalidateQueries({ queryKey: keys.folders() });
                void client.invalidateQueries({ queryKey: keys.messagesScope() });
                if (cancelled.draftId !== null) {
                  say('Schedule cancelled');
                  options.reopen(accountId, cancelled.draftId);
                } else {
                  say('Schedule cancelled. The message is in Drafts.');
                }
              },
              () => say('The schedule could not be cancelled. Cancel it from Scheduled.', { label: 'View', run: options.view }),
            );
          },
        },
        { label: 'View', run: options.view },
      );
    },
    [api, client, say],
  );

  const run = (action: Action | undefined) => () => {
    setNotice(undefined);
    action?.run();
  };

  const snackbar = (
    <Snackbar
      className="snackbar--scheduling"
      message={notice?.text}
      {...(notice ? { messageKey: `scheduling-${notice.id}` } : {})}
      {...(notice?.secondary
        ? {
            detail: (
              <button type="button" className="btn btn--ghost snackbar__action" onClick={run(notice.secondary)}>
                {notice.secondary.label}
              </button>
            ),
          }
        : {})}
      {...(notice?.action ? { actionLabel: notice.action.label, onAction: run(notice.action) } : {})}
      onDismiss={() => setNotice(undefined)}
      onHoldChange={setHeld}
    />
  );

  return {
    say: useCallback((text: string, action?: Action) => say(text, action), [say]),
    announceSnoozed,
    announceScheduled,
    snackbar: slot ? createPortal(snackbar, slot) : snackbar,
  };
}
